import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Download, Trash2, Phone, Mail, RefreshCw, Send } from "lucide-react";
import { API, api, authHeaders, formatApiError } from "../lib/api";
import { ECRequestsPanel } from "../components/admin/ECRequestsPanel";

const STATUSES = ["new", "contacted", "resolved"];
const BADGE = { new: "bg-amber-100 text-amber-800", contacted: "bg-sky-100 text-sky-800", resolved: "bg-emerald-100 text-emerald-800" };

const toCsv = (rows) => {
  const cols = ["created_at", "name", "phone", "email", "service", "status", "message"];
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  return [cols.join(","), ...rows.map((r) => cols.map((c) => esc(r[c])).join(","))].join("\n");
};

export default function AdminDashboard() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("enquiries");
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const handleErr = useCallback((err) => {
    if (err?.response?.status === 401) { localStorage.removeItem("ra_admin_token"); nav("/admin/login"); return; }
    toast.error(formatApiError(err?.response?.data?.detail));
  }, [nav]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`${API}/enquiries`, { headers: authHeaders() });
      setItems(Array.isArray(data) ? data : []);
    } catch (err) { handleErr(err); } finally { setLoading(false); }
  }, [handleErr]);

  useEffect(() => {
    if (!localStorage.getItem("ra_admin_token")) { nav("/admin/login"); return; }
    load();
  }, [load, nav]);

  const setStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`${API}/enquiries/${id}`, { status }, { headers: authHeaders() });
      setItems((xs) => xs.map((x) => (x.id === id ? data : x)));
      toast.success(`Marked as ${status}`);
    } catch (err) { handleErr(err); }
  };

  const [resending, setResending] = useState(null);
  const resend = async (id) => {
    setResending(id);
    try {
      const { data } = await api.post(`${API}/enquiries/${id}/resend-alert`, {}, { headers: authHeaders() });
      setItems((xs) => xs.map((x) => (x.id === id ? data : x)));
      if (data?.email_status === "sent") toast.success("Alert email sent");
      else toast.error("Alert still failed. Check that your email domain is set up.");
    } catch (err) { handleErr(err); } finally { setResending(null); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this enquiry?")) return;
    try {
      await api.delete(`${API}/enquiries/${id}`, { headers: authHeaders() });
      setItems((xs) => xs.filter((x) => x.id !== id));
      toast.success("Enquiry deleted");
    } catch (err) { handleErr(err); }
  };

  const shown = useMemo(() => (filter === "all" ? items : items.filter((x) => x.status === filter)), [items, filter]);
  const counts = useMemo(() => Object.fromEntries(STATUSES.map((s) => [s, items.filter((x) => x.status === s).length])), [items]);

  const exportCsv = () => {
    const url = URL.createObjectURL(new Blob([toCsv(shown)], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = "ridhanya-enquiries.csv"; a.click(); URL.revokeObjectURL(url);
  };

  const logout = () => { localStorage.removeItem("ra_admin_token"); nav("/admin/login"); };

  return (
    <div className="min-h-screen bg-[#F4EEE3]" data-testid="admin-dashboard">
      <header className="bg-[#070F1E] text-[#F4EEE3]">
        <div className="mx-auto max-w-[1300px] px-5 sm:px-8 py-5 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3" data-testid="admin-home-link">
            <img src="/logo192.png" alt="" className="h-10 w-10 rounded-full" />
            <span className="font-display text-2xl">Enquiry desk</span>
          </Link>
          <button onClick={logout} data-testid="admin-logout-btn" className="inline-flex items-center gap-2 rounded-full border border-[#C5A059]/40 px-4 py-2 text-sm hover:bg-white/5"><LogOut className="h-4 w-4" /> Sign out</button>
        </div>
      </header>
      <main className="mx-auto max-w-[1300px] px-5 sm:px-8 py-10">
        <div className="mb-8 inline-flex rounded-full border border-[#0A192F]/15 bg-white p-1" data-testid="admin-tabs">
          {[["enquiries", "Enquiries"], ["ec", "EC requests"]].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`admin-tab-${k}`}
              className={`rounded-full px-5 py-2 text-sm transition-colors ${tab === k ? "bg-[#0A192F] text-[#F4EEE3]" : "text-[#0A192F] hover:bg-[#F4EEE3]"}`}>{l}</button>
          ))}
        </div>
        {tab === "ec" ? <ECRequestsPanel handleErr={handleErr} /> : (<>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="admin-stats">
          {[["all", items.length], ...STATUSES.map((s) => [s, counts[s]])].map(([k, n]) => (
            <button key={k} onClick={() => setFilter(k)} data-testid={`admin-filter-${k}`}
              className={`text-left rounded-2xl border p-5 transition-colors ${filter === k ? "bg-[#0A192F] text-[#F4EEE3] border-[#0A192F]" : "bg-white border-[#0A192F]/10 text-[#0A192F] hover:border-[#8A6A2B]"}`}>
              <span className="block font-mono text-[11px] uppercase tracking-[0.22em] opacity-80">{k}</span>
              <span className="block mt-2 font-display text-4xl">{n}</span>
            </button>
          ))}
        </div>
        <div className="mt-8 flex items-center justify-between gap-4">
          <h1 className="font-display text-3xl text-[#0A192F]">{filter === "all" ? "All enquiries" : `${filter[0].toUpperCase()}${filter.slice(1)} enquiries`}</h1>
          <div className="flex gap-2">
            <button onClick={load} data-testid="admin-refresh-btn" className="grid h-10 w-10 place-items-center rounded-full border border-[#0A192F]/20 text-[#0A192F] hover:bg-white" aria-label="Refresh"><RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /></button>
            <button onClick={exportCsv} data-testid="admin-export-btn" className="inline-flex items-center gap-2 rounded-full bg-[#0A192F] px-4 py-2 text-sm text-[#F4EEE3]"><Download className="h-4 w-4" /> CSV</button>
          </div>
        </div>
        <div className="mt-6 space-y-4" data-testid="admin-enquiry-list">
          {!loading && shown.length === 0 && <p className="rounded-2xl bg-white p-10 text-center text-[#5A6578]" data-testid="admin-empty">No enquiries yet.</p>}
          {shown.map((e) => (
            <article key={e.id} className="rounded-2xl bg-white border border-[#0A192F]/10 p-6" data-testid={`admin-enquiry-${e.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <div className="flex flex-wrap items-center gap-3">
                    <h2 className="font-display text-2xl text-[#0A192F]">{e.name}</h2>
                    <span className={`rounded-full px-3 py-1 text-xs font-medium ${BADGE[e.status] ?? ""}`}>{e.status}</span>
                    <span className="text-xs text-[#5A6578]" data-testid={`admin-email-status-${e.id}`}>Email alert: {e.email_status}</span>
                    {e.email_status === "failed" && (
                      <button onClick={() => resend(e.id)} disabled={resending === e.id} data-testid={`admin-resend-alert-${e.id}`}
                        className="inline-flex items-center gap-1.5 rounded-full border border-[#8A6A2B]/50 px-3 py-1 text-xs text-[#8A6A2B] hover:bg-[#8A6A2B] hover:text-white transition-colors disabled:opacity-60">
                        <Send className={`h-3 w-3 ${resending === e.id ? "animate-pulse" : ""}`} /> {resending === e.id ? "Sending…" : "Resend alert"}
                      </button>
                    )}
                  </div>
                  <p className="mt-1 font-mono text-xs tracking-wider text-[#8A6A2B] uppercase">{e.service} · {new Date(e.created_at).toLocaleString()}</p>
                </div>
                <div className="flex items-center gap-2">
                  <select value={e.status} onChange={(ev) => setStatus(e.id, ev.target.value)} data-testid={`admin-status-select-${e.id}`} className="rounded-full border border-[#0A192F]/20 bg-white px-3 py-2 text-sm text-[#0A192F]">
                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <button onClick={() => remove(e.id)} data-testid={`admin-delete-${e.id}`} className="grid h-9 w-9 place-items-center rounded-full text-red-700 hover:bg-red-50" aria-label="Delete"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
              <p className="mt-4 text-[#2C3545] whitespace-pre-wrap">{e.message}</p>
              <div className="mt-4 flex flex-wrap gap-5 text-sm">
                <a href={`tel:${e.phone}`} className="inline-flex items-center gap-2 text-[#0A192F] hover:text-[#8A6A2B]"><Phone className="h-4 w-4" />{e.phone}</a>
                {e.email && <a href={`mailto:${e.email}`} className="inline-flex items-center gap-2 text-[#0A192F] hover:text-[#8A6A2B]"><Mail className="h-4 w-4" />{e.email}</a>}
              </div>
            </article>
          ))}
        </div>
        </>)}
      </main>
    </div>
  );
}
